import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import {
  AlertCircle,
  BadgeDollarSign,
  Banknote,
  Calculator,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Loader2,
  Repeat2,
  ShoppingBag,
  Sparkles,
  TrendingUp,
  Trophy,
  Wallet,
} from "lucide-react";
import axiosInstance from "../configApi/axiosConfig";
import { APIURL } from "../configApi/apiConfig";

const SLIDER_MAX = 200;
const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function fmtMoney(valor) {
  return `$${(Number(valor) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtNumber(valor) {
  return (Number(valor) || 0).toLocaleString("es-EC", { maximumFractionDigits: 0 });
}

function limitarPorcentaje(valor) {
  return Math.min(Math.max(Number(valor) || 0, 0), 100);
}

function periodoActual() {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
}

function nombrePeriodo(periodoStr) {
  const [mes, anio] = periodoStr.split("/");
  const nombre = MESES[parseInt(mes, 10) - 1];
  return nombre ? `${nombre} ${anio}` : periodoStr;
}

function shiftPeriodo(periodoStr, delta) {
  const [mesStr, anioStr] = periodoStr.split("/");
  let mes = parseInt(mesStr, 10);
  let anio = parseInt(anioStr, 10);
  if (!mes || !anio) return periodoStr;

  mes += delta;
  if (mes < 1) { mes = 12; anio -= 1; }
  if (mes > 12) { mes = 1; anio += 1; }
  return `${String(mes).padStart(2, "0")}/${anio}`;
}

function ProgressBar({ value, color = "bg-morado" }) {
  const width = limitarPorcentaje(value);
  return (
    <div
      className="h-2 overflow-hidden rounded-full bg-slate-100"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={Math.round(width)}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none ${color}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function SliderPorcentaje({ value, onChange }) {
  const fill = Math.min(100, (value / SLIDER_MAX) * 100);
  const color = value >= 100 ? "#059669" : "#2d3689";

  return (
    <div className="w-full">
      <div className="flex items-end justify-between gap-4">
        <p className="text-xs font-bold uppercase tracking-wide text-white/75">
          Simula tu cumplimiento
        </p>
        <output className="rounded-lg bg-white/10 px-3 py-1 text-xl font-black text-white" aria-live="polite">
          {value}%
        </output>
      </div>

      <div className="relative mt-3 flex h-8 items-center">
        <input
          type="range"
          min="0"
          max={SLIDER_MAX}
          step="1"
          value={Math.min(value, SLIDER_MAX)}
          onChange={(event) => onChange(Number(event.target.value))}
          aria-label="Porcentaje de cumplimiento a proyectar"
          className="sim-slider w-full cursor-pointer"
          style={{ "--sim-fill": `${fill}%`, "--sim-color": color }}
        />
        <span className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 text-[9px] font-bold text-white/60">
          META
        </span>
      </div>

      <style>{`
        .sim-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 9999px;
          background: linear-gradient(to right, var(--sim-color) var(--sim-fill), rgba(255,255,255,.2) var(--sim-fill));
          outline: none;
        }
        .sim-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: #ffffff;
          border: 3px solid var(--sim-color);
          box-shadow: 0 2px 8px rgba(15, 23, 42, .28);
          cursor: pointer;
        }
        .sim-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: #ffffff;
          border: 3px solid var(--sim-color);
          box-shadow: 0 2px 8px rgba(15, 23, 42, .28);
          cursor: pointer;
        }
        .sim-slider:focus-visible::-webkit-slider-thumb {
          outline: 3px solid rgba(255,255,255,.7);
          outline-offset: 3px;
        }
      `}</style>
    </div>
  );
}

function AvanceCard({ icon: Icon, title, current, goal, missing, percentage, color }) {
  const completada = missing <= 0 && goal > 0;
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${color}`}>
            <Icon size={17} className="text-white" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            <p className="text-xs text-slate-500">{fmtMoney(current)} de {fmtMoney(goal)}</p>
          </div>
        </div>
        <span className="text-2xl font-black tracking-tight text-slate-800">{Math.round(percentage)}%</span>
      </div>
      <div className="mt-4"><ProgressBar value={percentage} color={color} /></div>
      <p className={`mt-2 text-xs font-semibold ${completada ? "text-emerald-700" : "text-slate-600"}`}>
        {completada ? "Meta alcanzada" : `Te faltan ${fmtMoney(missing)}`}
      </p>
    </article>
  );
}

function GananciaRow({ icon: Icon, title, detail, possible, current, progress, color = "bg-morado", highlighted = false }) {
  return (
    <article className={`rounded-2xl border p-4 border-slate-200 bg-white`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${highlighted ? `${color} text-white` : "bg-slate-100 text-slate-600"}`}>
            <Icon size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            <p className="mt-0.5 text-xs leading-5 text-slate-500">{detail}</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Puedes ganar</p>
          <p className={`text-lg font-black text-slate-800`}>{fmtMoney(possible)}</p>
          {current !== null && current !== undefined && (
            <p className="text-[11px] font-semibold text-emerald-700">Actual: {fmtMoney(current)}</p>
          )}
        </div>
      </div>
      {progress !== null && progress !== undefined && (
        <div className="mt-3"><ProgressBar value={progress} color={color} /></div>
      )}
    </article>
  );
}

export default function SimuladorMetas() {
  const [periodo, setPeriodo] = useState(periodoActual);
  const [porcentaje, setPorcentaje] = useState(130);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const debounceRef = useRef(null);
  const primerCargaRef = useRef(true);
  const comisionActualCacheRef = useRef(new Map());

  const esPeriodoFuturo = (periodoEvaluado) => {
    const [mesStr, anioStr] = periodoEvaluado.split("/");
    const mes = parseInt(mesStr, 10);
    const anio = parseInt(anioStr, 10);
    const now = new Date();
    if (!mes || !anio) return false;
    return anio > now.getFullYear() || (anio === now.getFullYear() && mes > now.getMonth() + 1);
  };

  const irAPeriodo = (delta) => {
    const candidato = shiftPeriodo(periodo, delta);
    if (delta > 0 && esPeriodoFuturo(candidato)) return;
    setPeriodo(candidato);
  };

  const simular = async () => {
    setErrorMsg("");
    if (esPeriodoFuturo(periodo)) {
      setErrorMsg("Ese periodo aún no está habilitado porque corresponde a un mes futuro.");
      setResultado(null);
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIURL.postReporteSimuladorCredito(periodo, porcentaje));
      const fila = response.data?.[0] || {};

      const metaMensual = Number(fila.MetaMensual) || 0;
      const ventaAcumulada = Number(fila.VentaAcumulada) || 0;
      const porcentajeActual = metaMensual > 0 ? (ventaAcumulada / metaMensual) * 100 : 0;
      const cacheKey = `${periodo}-${ventaAcumulada}-${metaMensual}`;
      let comisionActual = 0;

      if (porcentajeActual > 0) {
        if (Math.abs(porcentajeActual - porcentaje) < 0.01) {
          comisionActual = Number(fila.ComisionProyectada) || 0;
        } else if (comisionActualCacheRef.current.has(cacheKey)) {
          comisionActual = comisionActualCacheRef.current.get(cacheKey);
        } else {
          try {
            const responseActual = await axiosInstance.post(
              APIURL.postReporteSimuladorCredito(periodo, Number(porcentajeActual.toFixed(2)))
            );
            comisionActual = Number(responseActual.data?.[0]?.ComisionProyectada) || 0;
            comisionActualCacheRef.current.set(cacheKey, comisionActual);
          } catch (errorComisionActual) {
            console.warn("No se pudo calcular la comisión actual:", errorComisionActual);
          }
        }
      }

      setResultado({
        periodo,
        porcentaje,
        metaMensual,
        metaDiaria: Number(fila.MetaDiaria) || 0,
        venta: Number(fila.Venta) || 0,
        ventaAcumulada,
        ventaHoy: Number(fila.VentaHoy) || 0,
        faltante: Number(fila.Faltante) || 0,
        comisionProyectada: Number(fila.ComisionProyectada) || 0,
        comisionActual,
        tasaComision: Number(fila.TasaComision) || 0,
        bonoComision: Number(fila.BonoComision) || 0,
        metaMensualCredito: Number(fila.MetaMensualCredito) || 0,
        ventaCreditoAcumulada: Number(fila.VentaCreditoAcumulada) || 0,
        porcentajeCreditoReal: Number(fila.PorcentajeCreditoReal) || 0,
        metaSemanal: Number(fila.MetaSemanal) || 0,
        ventaSemanal: Number(fila.VentaSemanal) || 0,
        porcentajeSemanal: Number(fila.PorcentajeSemanal) || 0,
        metaSemanalCredito: Number(fila.MetaSemanalCredito) || 0,
        ventaSemanalCredito: Number(fila.VentaSemanalCredito) || 0,
        porcentajeSemanalCredito: Number(fila.PorcentajeSemanalCredito) || 0,
        bonoSemanalPosible: Number(fila.BonoSemanalPosible) || 0,
        bonoSemanalGanado: Number(fila.BonoSemanalGanado) || 0,
        metaBancarizado: Number(fila.MetaBancarizado) || 0,
        actualBancarizado: Number(fila.ActualBancarizado) || 0,
        faltanteBancarizado: Number(fila.FaltanteBancarizado) || 0,
        bonoPosibleBancarizado: Number(fila.BonoPosibleBancarizado) || 0,
        bonoActualBancarizado: Number(fila.BonoActualBancarizado) || 0,
        metaRecurrente: Number(fila.MetaRecurrente) || 0,
        actualRecurrente: Number(fila.ActualRecurrente) || 0,
        faltanteRecurrente: Number(fila.FaltanteRecurrente) || 0,
        bonoPosibleRecurrente: Number(fila.BonoPosibleRecurrente) || 0,
        bonoActualRecurrente: Number(fila.BonoActualRecurrente) || 0,
        metaYupana: Number(fila.MetaYupana) || 0,
        ventaYupanaAcumulada: Number(fila.VentaYupanaAcumulada) || 0,
        porcentajeYupanaReal: Number(fila.PorcentajeYupanaReal) || 0,
        faltanteYupana: Number(fila.FaltanteYupana) || 0,
        bonoYupanaActual: Number(fila.BonoYupanaActual) || 0,
        bonoYupanaPosible: Number(fila.BonoYupanaPosible) || 0,
      });
    } catch (error) {
      console.error("Error al simular proyección:", error);
      setErrorMsg("No se pudo calcular la proyección. Intenta nuevamente.");
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(simular, primerCargaRef.current ? 0 : 300);
    primerCargaRef.current = false;
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, porcentaje]);

  const siguienteEsFuturo = esPeriodoFuturo(shiftPeriodo(periodo, 1));
  const sinComisionDefinida = !!resultado && resultado.tasaComision === 0 && resultado.bonoComision === 0;
  const porcentajeVenta = resultado?.metaMensual > 0 ? (resultado.ventaAcumulada / resultado.metaMensual) * 100 : 0;
  const faltanteCredito = resultado ? Math.max(resultado.metaMensualCredito - resultado.ventaCreditoAcumulada, 0) : 0;
  const progresoBancarizado = resultado ? (resultado.actualBancarizado / Math.max(resultado.metaBancarizado + 1, 1)) * 100 : 0;
  const progresoRecurrente = resultado ? (resultado.actualRecurrente / Math.max(resultado.metaRecurrente + 1, 1)) * 100 : 0;
  const progresoSemanal = resultado ? Math.min(resultado.porcentajeSemanal, resultado.porcentajeSemanalCredito) : 0;

  const totalBonosPosibles = resultado
    ? resultado.bonoSemanalPosible + resultado.bonoPosibleBancarizado
      + resultado.bonoPosibleRecurrente + resultado.bonoYupanaPosible
    : 0;
  const totalBonosActuales = resultado
    ? resultado.bonoSemanalGanado + resultado.bonoActualBancarizado
      + resultado.bonoActualRecurrente + resultado.bonoYupanaActual
    : 0;
  const totalPotencial = resultado ? resultado.comisionProyectada + totalBonosPosibles : 0;
  const totalGanandoActual = resultado ? resultado.comisionActual + totalBonosActuales : 0;

  const detalleBancarizado = resultado?.faltanteBancarizado > 0
    ? `${fmtNumber(resultado.actualBancarizado)} clientes · Faltan ${fmtNumber(resultado.faltanteBancarizado)}`
    : `${fmtNumber(resultado?.actualBancarizado)} clientes · Meta superada`;
  const detalleRecurrente = resultado?.faltanteRecurrente > 0
    ? `${fmtNumber(resultado.actualRecurrente)} clientes · Faltan ${fmtNumber(resultado.faltanteRecurrente)}`
    : `${fmtNumber(resultado?.actualRecurrente)} clientes · Meta superada`;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 font-sans">
      <Layout />
      <main className="flex-1 p-4 md:p-6 md:pt-4">
        <div className="mx-auto max-w-5xl space-y-4">
          <header className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-morado px-5 py-4 text-white shadow-lg">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><Calculator size={19} /></div>
              <div>
                <h1 className="text-lg font-black leading-tight">Simulador de metas</h1>
                <p className="mt-0.5 text-xs text-slate-300">Tu avance y todas tus oportunidades de ganancia</p>
              </div>
            </div>

            <div className="flex items-center rounded-xl border border-white/10 bg-white/10 p-1">
              <button type="button" onClick={() => irAPeriodo(-1)} aria-label="Ir al mes anterior" className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70">
                <ChevronLeft size={18} />
              </button>
              <div className="min-w-[124px] px-2 text-center">
                <p className="text-sm font-bold">{nombrePeriodo(periodo)}</p>
                <label className="mt-0.5 flex items-center justify-center gap-1 text-slate-300">
                  <Calendar size={10} />
                  <span className="sr-only">Periodo en formato mes y año</span>
                  <input type="text" value={periodo} onChange={(event) => setPeriodo(event.target.value)} aria-label="Periodo en formato mes y año" className="w-16 bg-transparent text-center text-[11px] outline-none focus:text-white" />
                </label>
              </div>
              <button type="button" onClick={() => irAPeriodo(1)} disabled={siguienteEsFuturo} aria-label="Ir al mes siguiente" className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:cursor-not-allowed disabled:opacity-30">
                <ChevronRight size={18} />
              </button>
            </div>
          </header>

          {errorMsg && (
            <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              <AlertCircle size={17} className="mt-0.5 flex-shrink-0" /><p>{errorMsg}</p>
            </div>
          )}

          {loading && !resultado && (
            <div className="flex min-h-52 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm">
              <Loader2 size={22} className="animate-spin text-morado motion-reduce:animate-none" />
              <span className="text-sm font-semibold">Calculando tus ganancias...</span>
            </div>
          )}

          {resultado && (
            <div aria-live="polite" className={`space-y-4 transition-opacity duration-200 motion-reduce:transition-none ${loading ? "opacity-60" : "opacity-100"}`}>
              <section className="overflow-hidden rounded-2xl bg-morado text-white shadow-lg">
                <div className="grid gap-6 p-5 md:grid-cols-[1fr_0.9fr] md:items-center md:p-6">
                  <div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/70">
                          <Sparkles size={14} /> Potencial del periodo
                        </div>
                        <p className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">{fmtMoney(totalPotencial)}</p>
                        <p className="mt-1 text-xs leading-5 text-white/75">Si llegas al {resultado.porcentaje}% y cumples los bonos.</p>
                      </div>

                      <div className="rounded-2xl bg-white p-4 text-morado shadow-sm">
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-morado/70">
                          <BadgeDollarSign size={14} /> Ganando actualmente
                        </div>
                        <p className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">{fmtMoney(totalGanandoActual)}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-600">Comisión actual más bonos ya alcanzados.</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                    <SliderPorcentaje value={porcentaje} onChange={setPorcentaje} />
                    {loading && <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-white/75"><Loader2 size={13} className="animate-spin motion-reduce:animate-none" /> Actualizando proyección...</p>}
                  </div>
                </div>
              </section>

              {sinComisionDefinida && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <p>No hay una comisión configurada para el {resultado.porcentaje}%. Los demás bonos sí están incluidos.</p>
                </div>
              )}

              <section aria-labelledby="avance-title">
                <div className="mb-3 px-1">
                  <h2 id="avance-title" className="text-base font-black text-slate-900">Tu avance</h2>
                </div>

                <div className="mb-3 flex items-center justify-between gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-morado text-white">
                      <Banknote size={20} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-morado/70">Venta de hoy</p>
                      <p className="text-xs text-slate-600">Se suma a tu avance general</p>
                    </div>
                  </div>
                  <p className="text-2xl font-black tracking-tight text-morado sm:text-3xl">{fmtMoney(resultado.ventaHoy)}</p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <AvanceCard icon={TrendingUp} title="Venta general" current={resultado.ventaAcumulada} goal={resultado.metaMensual} missing={Math.max(resultado.metaMensual - resultado.ventaAcumulada, 0)} percentage={porcentajeVenta} color="bg-morado" />
                  <AvanceCard icon={CreditCard} title="Venta crédito" current={resultado.ventaCreditoAcumulada} goal={resultado.metaMensualCredito} missing={faltanteCredito} percentage={resultado.porcentajeCreditoReal} color="bg-emerald-600" />
                </div>
              </section>

              <section aria-labelledby="ganancias-title">
                <div className="mb-3 px-1"><h2 id="ganancias-title" className="text-base font-black text-slate-900">De dónde sale tu ganancia</h2></div>
                <div className="grid gap-3 lg:grid-cols-2">
                  <GananciaRow icon={Wallet} title="Comisión mensual" detail={`Proyección al ${resultado.porcentaje}% · Avance real ${Math.round(porcentajeVenta)}%`} possible={resultado.comisionProyectada} current={resultado.comisionActual} progress={resultado.porcentaje} color="bg-primaryPurple"  highlighted/>
                  <GananciaRow icon={Trophy} title="Bono semanal" detail={`Ventas ${Math.round(resultado.porcentajeSemanal)}% · Crédito ${Math.round(resultado.porcentajeSemanalCredito)}%`} possible={resultado.bonoSemanalPosible} current={resultado.bonoSemanalGanado} progress={progresoSemanal} color="bg-amber-500"  highlighted/>
                  <GananciaRow icon={ShoppingBag} title="Clientes bancarizados" detail={detalleBancarizado} possible={resultado.bonoPosibleBancarizado} current={resultado.bonoActualBancarizado} progress={progresoBancarizado} color="bg-blue-600" highlighted/>
                  <GananciaRow icon={Repeat2} title="Clientes recurrentes" detail={detalleRecurrente} possible={resultado.bonoPosibleRecurrente} current={resultado.bonoActualRecurrente} progress={progresoRecurrente} color="bg-cyan-600" highlighted/>
                  <GananciaRow icon={BadgeDollarSign} title="Comisión Yupana" detail={`${fmtMoney(resultado.ventaYupanaAcumulada)} de ${fmtMoney(resultado.metaYupana)} · Faltan ${fmtMoney(resultado.faltanteYupana)}`} possible={resultado.bonoYupanaPosible} current={resultado.bonoYupanaActual} progress={resultado.porcentajeYupanaReal} color="bg-emerald-600" highlighted/>
                  
                </div>
              </section>

              
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
